import { apiClient } from '../utils/api-client'
import { throwForResponse } from './api-error'

/**
 * The Article 28 agreement a gym signs before we will serve its roster.
 *
 * Reachable while the gym is still blocked — it is the way out of the block,
 * so it must never be gated on the thing it exists to fix.
 */

export interface DpaSignature {
  version: string
  signed_at: string
  legal_name: string
  legal_address: string
  tax_id: string
  signer_name: string
  signer_role: string | null
}

export interface DpaStatus {
  /** The version in force — what a new signature binds. */
  required_version: string
  /**
   * Whether the data is served at all: a signature on a text still honoured.
   * One older than that counts as unsigned.
   */
  signed: boolean
  /**
   * Whether the signature is on `required_version`. Signed but not current is a
   * place still served, asked to read and sign the new text — what the new text
   * adds stays off for it until it does.
   */
  current: boolean
  /** The most recent signature, whatever version it was on. */
  latest: DpaSignature | null
}

export interface DpaAcceptancePayload {
  legal_name: string
  legal_address: string
  tax_id: string
  signer_name: string
  signer_role?: string
  locale: string
}

class DpaService {
  async status(gymSlug: string): Promise<DpaStatus> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/dpa_acceptance`)
    if (!response.ok) await throwForResponse(response, 'Failed to load the agreement status')
    return response.json()
  }

  async accept(gymSlug: string, payload: DpaAcceptancePayload): Promise<DpaStatus> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/dpa_acceptance`, payload)
    if (!response.ok) await throwForResponse(response, 'Failed to record the agreement')
    return response.json()
  }
}

export const dpaService = new DpaService()
